// src/pages/seasons/SeasonDetail.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { db } from '@/firebase/firebase';
import { doc, getDoc, updateDoc, serverTimestamp, collection, query, where, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  ArrowLeftIcon,
  LinkIcon,
  ClipboardDocumentListIcon,
  ClipboardDocumentCheckIcon,
  CalendarDaysIcon,
  LockOpenIcon,
  LockClosedIcon,
  PencilSquareIcon,
  CheckIcon,
  XMarkIcon,
  ArchiveBoxIcon,
  ArrowUturnLeftIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  EnvelopeIcon,
  SparklesIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import TeamSubNav from '@/components/team/TeamSubNav';
import { getActivePricingTier, formatTierPrice } from '@/utils/pricingTiers';
import { toSeasonDate, toDateInputValue } from '@/utils/seasonDates';
import { activeLocale } from '@/utils/formatLocale';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

function fmtTierDate(v) {
  const d = toSeasonDate(v);
  return d ? d.toLocaleDateString() : null;
}

export default function SeasonDetail() {
  const { seasonId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [season,   setSeason]   = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [editing,  setEditing]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [form,     setForm]     = useState({});
  const [regCount, setRegCount] = useState(null);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [showInviteModal,  setShowInviteModal]  = useState(false);

  // Live registration count — teamId required by security rules
  useEffect(() => {
    if (!seasonId || !season?.teamId) return;
    const q = query(
      collection(db, 'registrations'),
      where('teamId',   '==', season.teamId),
      where('seasonId', '==', seasonId)
    );
    const unsub = onSnapshot(q, (snap) => {
      const active = snap.docs.filter(d => !['cancelled', 'archived'].includes(d.data().status));
      setRegCount(active.length);
    }, console.error);
    return unsub;
  }, [seasonId, season?.teamId]);

  useEffect(() => {
    getDoc(doc(db, 'seasons', seasonId)).then(snap => {
      if (snap.exists()) {
        const data = { id: snap.id, ...snap.data() };
        setSeason(data);
        setForm({
          name: data.name || '',
          // != null, not truthiness: a $0 (free) season must hydrate as "0",
          // not an empty field that looks unset (funnel-walk finding #6).
          registrationFee: data.registrationFee != null ? (data.registrationFee / 100).toFixed(0) : '',
          registrationOpen: toDateInputValue(data.registrationOpen),
          registrationClose: toDateInputValue(data.registrationClose),
          seasonStart: toDateInputValue(data.seasonStart),
          seasonEnd: toDateInputValue(data.seasonEnd),
          maxPlayers: data.maxPlayers || '',
          waiverText: data.waiverText || '',
        });
      }
      setLoading(false);
    });
  }, [seasonId]);

  const toggleStatus = async () => {
    const newStatus = season.status === 'open' ? 'closed' : 'open';
    try {
      await updateDoc(doc(db, 'seasons', seasonId), { status: newStatus });
      setSeason(s => ({ ...s, status: newStatus }));
      toast.success(newStatus === 'open' ? t('seasonDetail.toast.regOpened') : t('seasonDetail.toast.regClosed'));
    } catch {
      toast.error(t('seasonDetail.toast.statusFail'));
    }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/register/${season.teamId}/${seasonId}`;
    navigator.clipboard.writeText(url).then(() => toast.success(t('seasonDetail.toast.linkCopied')));
  };

  const saveEdits = async () => {
    setSaving(true);
    try {
      const feeInCents = form.registrationFee
        ? Math.round(parseFloat(form.registrationFee) * 100)
        : 0;
      const updates = {
        name: form.name.trim(),
        registrationFee: feeInCents,
        registrationOpen: toSeasonDate(form.registrationOpen),
        registrationClose: toSeasonDate(form.registrationClose),
        seasonStart: toSeasonDate(form.seasonStart),
        seasonEnd: toSeasonDate(form.seasonEnd),
        maxPlayers: form.maxPlayers ? parseInt(form.maxPlayers) : null,
        waiverText: form.waiverText.trim() || null,
        updatedAt: serverTimestamp(),
      };
      await updateDoc(doc(db, 'seasons', seasonId), updates);
      setSeason(s => ({ ...s, ...updates, registrationFee: feeInCents }));
      setEditing(false);
      toast.success(t('seasonDetail.toast.updated'));
    } catch (err) {
      toast.error(t('seasonDetail.toast.saveFail'));
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    const isArchived = season.status === 'archived';
    const next = isArchived ? 'closed' : 'archived';
    try {
      await updateDoc(doc(db, 'seasons', seasonId), { status: next, updatedAt: serverTimestamp() });
      setSeason(s => ({ ...s, status: next }));
      toast.success(isArchived ? t('seasonDetail.toast.restored') : t('seasonDetail.toast.archived'));
      setConfirmArchive(false);
      if (!isArchived) navigate(season?.teamId ? `/teams/${season.teamId}/seasons` : '/seasons');
    } catch {
      toast.error(t('seasonDetail.toast.archiveFail'));
    }
  };

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f7faf5]">
        <div className="max-w-2xl mx-auto px-4 py-8 space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="h-24 bg-zinc-100 rounded-2xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  if (!season) {
    return (
      <div className="min-h-screen bg-[#f7faf5]">
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <p className="text-zinc-500">{t('seasonDetail.notFound')}</p>
          <button
            onClick={() => navigate(season?.teamId ? `/teams/${season.teamId}/seasons` : '/seasons')}
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold uppercase text-[#5a8a1f] hover:text-[#3d6010]"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
          >
            <ArrowLeftIcon className="h-4 w-4" /> {t('seasonDetail.backToSeasons')}
          </button>
        </div>
      </div>
    );
  }

  const isOpen     = season.status === 'open';
  const isArchived = season.status === 'archived';
  // Draft (auto-created, never opened) must not masquerade as "Closed" —
  // the team card and Home checklist both call this state a draft.
  const isDraft    = season.status === 'draft';
  const fee        = season.registrationFee > 0 ? `$${(season.registrationFee / 100).toFixed(0)}` : t('seasonDetail.free');
  const maxPlayers = season.maxPlayers ?? null;
  const capPct     = (maxPlayers && regCount !== null) ? Math.min(100, Math.round((regCount / maxPlayers) * 100)) : null;
  const capColor   = capPct >= 90 ? 'bg-red-500' : capPct >= 75 ? 'bg-amber-400' : 'bg-[#b5f03d]';

  const fmtDate = (v) => fmtTierDate(v) || '—';

  return (
    <div className="min-h-screen bg-[#f7faf5]">
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* Team sub-nav: keeps Overview/Roster/Schedule/Chat tabs visible while drilled into team-scoped content. */}
        <TeamSubNav teamId={season?.teamId} />
        {/* Back link */}
        <button
          onClick={() => navigate(season?.teamId ? `/teams/${season.teamId}/seasons` : '/seasons')}
          className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-[#5a8a1f] transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" /> {t('seasonDetail.seasons')}
        </button>

        {/* Season header card — pitch-dark with lime medallion */}
        <div className="bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden">
          {/* Pitch-dark header */}
          <div className="px-5 pt-5 pb-5 bg-[#0d1f14] relative overflow-hidden">
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.05]"
              style={{
                backgroundImage:
                  'linear-gradient(to right, #b5f03d 1px, transparent 1px), linear-gradient(to bottom, #b5f03d 1px, transparent 1px)',
                backgroundSize: '32px 32px',
              }}
            />
            <div className="relative flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                  <CalendarDaysIcon className="h-6 w-6 text-[#b5f03d]" />
                </div>
                <div className="min-w-0">
                  <p
                    className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#b5f03d] mb-1"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    {t('seasonDetail.eyebrow')}
                  </p>
                  <h1
                    className="uppercase text-white leading-[1.05] truncate"
                    style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 24 }}
                  >
                    {season.name}
                  </h1>
                </div>
              </div>
              {!editing && (
                <button
                  onClick={() => setEditing(true)}
                  className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#b5f03d]/40 bg-[#b5f03d]/10 text-[#b5f03d] text-xs font-bold uppercase hover:bg-[#b5f03d]/20 transition-colors"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  <PencilSquareIcon className="h-3.5 w-3.5" />
                  {t('seasonDetail.edit')}
                </button>
              )}
            </div>

            {/* Status chip + meta row */}
            <div className="relative mt-4 flex items-center gap-2 flex-wrap">
              <span
                className={clsx(
                  'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase border',
                  isArchived
                    ? 'bg-zinc-700/50 text-zinc-300 border-zinc-600'
                    : isOpen
                      ? 'bg-[#b5f03d]/15 text-[#b5f03d] border-[#b5f03d]/40'
                      : isDraft
                        ? 'bg-amber-400/15 text-amber-300 border-amber-400/40'
                        : 'bg-zinc-700/50 text-zinc-300 border-zinc-600'
                )}
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
              >
                {isArchived ? <ArchiveBoxIcon className="h-3 w-3" /> : isOpen ? <LockOpenIcon className="h-3 w-3" /> : <LockClosedIcon className="h-3 w-3" />}
                {isArchived ? t('seasonDetail.statusArchived') : isOpen ? t('seasonDetail.statusOpen') : isDraft ? t('seasonDetail.statusDraft') : t('seasonDetail.statusClosed')}
              </span>
              <span className="text-[11px] font-bold uppercase text-white/60" style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}>{fee}</span>
              {regCount !== null && (
                <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase text-white/60" style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}>
                  <UserGroupIcon className="h-3 w-3" />
                  {maxPlayers
                    ? t('seasonDetail.registeredOfMax', { count: regCount, max: maxPlayers })
                    : t('seasonDetail.registered', { count: regCount })}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div>
          <p
            className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-2"
            style={{ fontFamily: HEAD_FONT }}
          >
            {t('seasonDetail.quickActions.title')}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <button
              onClick={toggleStatus}
              className="group flex items-start gap-3 rounded-2xl border border-zinc-200 bg-white p-4 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] hover:border-[#b5f03d]/40 hover:bg-[#b5f03d]/5 transition-all text-left"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                {isOpen
                  ? <LockClosedIcon className="h-5 w-5 text-[#5a8a1f]" />
                  : <LockOpenIcon className="h-5 w-5 text-[#5a8a1f]" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-[#0d1f14] group-hover:text-[#3d6010] transition-colors">
                  {isOpen ? t('seasonDetail.quickActions.closeReg') : t('seasonDetail.quickActions.openReg')}
                </p>
                <p className="text-xs text-zinc-500">{isOpen ? t('seasonDetail.quickActions.closeRegSub') : t('seasonDetail.quickActions.openRegSub')}</p>
              </div>
            </button>

            <button
              onClick={() => setShowInviteModal(true)}
              className="group flex items-start gap-3 rounded-2xl border border-zinc-200 bg-white p-4 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] hover:border-[#b5f03d]/40 hover:bg-[#b5f03d]/5 transition-all text-left"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                <EnvelopeIcon className="h-5 w-5 text-[#5a8a1f]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-[#0d1f14] group-hover:text-[#3d6010] transition-colors">{t('seasonDetail.quickActions.shareInvite')}</p>
                <p className="text-xs text-zinc-500">{t('seasonDetail.quickActions.shareInviteSub')}</p>
              </div>
            </button>

            <button
              onClick={copyLink}
              className="group flex items-start gap-3 rounded-2xl border border-zinc-200 bg-white p-4 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] hover:border-[#b5f03d]/40 hover:bg-[#b5f03d]/5 transition-all text-left"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                <LinkIcon className="h-5 w-5 text-[#5a8a1f]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-[#0d1f14] group-hover:text-[#3d6010] transition-colors">{t('seasonDetail.quickActions.copyLink')}</p>
                <p className="text-xs text-zinc-500">{t('seasonDetail.quickActions.copyLinkSub')}</p>
              </div>
            </button>

            <Link
              to={`/seasons/${seasonId}/registrations`}
              className="group flex items-start gap-3 rounded-2xl border border-zinc-200 bg-white p-4 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] hover:border-[#b5f03d]/40 hover:bg-[#b5f03d]/5 transition-all"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                <ClipboardDocumentListIcon className="h-5 w-5 text-[#5a8a1f]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-[#0d1f14] group-hover:text-[#3d6010] transition-colors">{t('seasonDetail.quickActions.registrations')}</p>
                <p className="text-xs text-zinc-500">{t('seasonDetail.quickActions.registrationsSub')}</p>
              </div>
            </Link>

            <Link
              to={`/seasons/${seasonId}/attendance`}
              className="group flex items-start gap-3 rounded-2xl border border-zinc-200 bg-white p-4 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] hover:border-[#b5f03d]/40 hover:bg-[#b5f03d]/5 transition-all"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                <ClipboardDocumentCheckIcon className="h-5 w-5 text-[#5a8a1f]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-[#0d1f14] group-hover:text-[#3d6010] transition-colors">{t('seasonDetail.quickActions.attendance')}</p>
                <p className="text-xs text-zinc-500">{t('seasonDetail.quickActions.attendanceSub')}</p>
              </div>
            </Link>

            <Link
              to={`/seasons/${seasonId}/schedule`}
              className="group flex items-start gap-3 rounded-2xl border border-zinc-200 bg-white p-4 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] hover:border-[#b5f03d]/40 hover:bg-[#b5f03d]/5 transition-all"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                <CalendarDaysIcon className="h-5 w-5 text-[#5a8a1f]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-[#0d1f14] group-hover:text-[#3d6010] transition-colors">{t('seasonDetail.quickActions.schedule')}</p>
                <p className="text-xs text-zinc-500">{t('seasonDetail.quickActions.scheduleSub')}</p>
              </div>
            </Link>
          </div>
        </div>

        {/* Capacity bar */}
        {capPct !== null && (
          <div className="bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-5">
            <div className="flex items-center justify-between mb-2 text-sm">
              <span className="font-semibold text-[#0d1f14] flex items-center gap-1.5">
                <UserGroupIcon className="h-4 w-4 text-[#5a8a1f]" />
                {t('seasonDetail.capacity.title')}
              </span>
              <span className={clsx(
                'font-semibold tabular-nums',
                capPct >= 90 ? 'text-red-600' : capPct >= 75 ? 'text-amber-600' : 'text-[#3d6010]'
              )}>
                {t('seasonDetail.capacity.value', { count: regCount, max: maxPlayers, pct: capPct })}
              </span>
            </div>
            <div className="h-2.5 rounded-full bg-zinc-100 overflow-hidden">
              <div
                className={clsx('h-full rounded-full transition-all', capColor)}
                style={{ width: `${capPct}%` }}
              />
            </div>
            {capPct >= 90 && (
              <p className="mt-1.5 text-xs text-red-600 font-medium">{t('seasonDetail.capacity.almostFull')}</p>
            )}
          </div>
        )}

        {/* Details / Edit Form */}
        <div className="bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h2
              className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
              style={{ fontFamily: HEAD_FONT }}
            >
              {t('seasonDetail.details.title')}
            </h2>
            {editing && (
              <div className="flex gap-2">
                <button
                  onClick={() => setEditing(false)}
                  className="inline-flex items-center gap-1 rounded-xl border border-zinc-200 bg-white px-3 py-1.5 text-xs font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                  {t('seasonDetail.details.cancel')}
                </button>
                <button
                  onClick={saveEdits}
                  disabled={saving}
                  className="inline-flex items-center gap-1 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-3 py-1.5 text-xs font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-50"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                >
                  <CheckIcon className="h-3.5 w-3.5" />
                  {saving ? t('seasonDetail.details.saving') : t('seasonDetail.details.save')}
                </button>
              </div>
            )}
          </div>

          {editing ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#0d1f14] mb-1">{t('seasonDetail.details.nameLabel')}</label>
                <input type="text" value={form.name} onChange={set('name')}
                  className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#0d1f14] mb-1">{t('seasonDetail.details.feeLabel')}</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">$</span>
                    <input type="number" min="0" step="1" value={form.registrationFee} onChange={set('registrationFee')}
                      className="w-full rounded-lg border border-zinc-200 pl-7 pr-3 py-2 text-sm focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0d1f14] mb-1">{t('seasonDetail.details.maxPlayersLabel')}</label>
                  <input type="number" min="1" value={form.maxPlayers} onChange={set('maxPlayers')}
                    className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#0d1f14] mb-1">{t('seasonDetail.details.regOpensLabel')}</label>
                  <input type="date" value={form.registrationOpen} onChange={set('registrationOpen')}
                    className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0d1f14] mb-1">{t('seasonDetail.details.regClosesLabel')}</label>
                  <input type="date" value={form.registrationClose} onChange={set('registrationClose')}
                    className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#0d1f14] mb-1">{t('seasonDetail.details.seasonStartLabel')}</label>
                  <input type="date" value={form.seasonStart} onChange={set('seasonStart')}
                    className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0d1f14] mb-1">{t('seasonDetail.details.seasonEndLabel')}</label>
                  <input type="date" value={form.seasonEnd} onChange={set('seasonEnd')}
                    className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0d1f14] mb-1">{t('seasonDetail.details.waiverLabel')}</label>
                <textarea value={form.waiverText} onChange={set('waiverText')} rows={4}
                  className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30 resize-none" />
                {/* Sample-template disclaimer — CreateSeason carries the full
                    amber warning, but auto-draft coaches never visit that
                    form; this page is their only waiver surface, so the
                    disclaimer must live here too (2026-07-24 liability
                    audit). */}
                <p className="mt-1.5 text-[11px] leading-snug text-amber-700">
                  {t('seasonDetail.details.waiverSample')}
                </p>
              </div>
              {/* Bottom action row (#7, funnel-walk): the only Save/Cancel
                  used to live at the TOP of this long form, invisible from
                  the waiver textarea. Same handlers, duplicated in reach. */}
              <div className="flex justify-end gap-2 pt-1">
                <button
                  onClick={() => setEditing(false)}
                  className="inline-flex items-center gap-1 rounded-xl border border-zinc-200 bg-white px-3 py-1.5 text-xs font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  {t('seasonDetail.details.cancel')}
                </button>
                <button
                  onClick={saveEdits}
                  disabled={saving}
                  className="inline-flex items-center gap-1 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-3 py-1.5 text-xs font-bold uppercase transition-all active:scale-[0.97] disabled:opacity-50"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                >
                  {saving ? t('seasonDetail.details.saving') : t('seasonDetail.details.save')}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                  <CurrencyDollarIcon className="h-4 w-4 text-[#5a8a1f]" />
                </div>
                <span><span className="font-semibold text-[#0d1f14]">{t('seasonDetail.details.registrationFee')}</span> <span className="text-zinc-700">{fee}</span></span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                  <UserGroupIcon className="h-4 w-4 text-[#5a8a1f]" />
                </div>
                <span><span className="font-semibold text-[#0d1f14]">{t('seasonDetail.details.maxPlayers')}</span> <span className="text-zinc-700">{season.maxPlayers || t('seasonDetail.unlimited')}</span></span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                  <LockOpenIcon className="h-4 w-4 text-[#5a8a1f]" />
                </div>
                <span><span className="font-semibold text-[#0d1f14]">{t('seasonDetail.details.registrationOpens')}</span> <span className="text-zinc-700">{fmtDate(season.registrationOpen)}</span></span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                  <LockClosedIcon className="h-4 w-4 text-[#5a8a1f]" />
                </div>
                <span><span className="font-semibold text-[#0d1f14]">{t('seasonDetail.details.registrationCloses')}</span> <span className="text-zinc-700">{fmtDate(season.registrationClose)}</span></span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                  <CalendarIcon className="h-4 w-4 text-[#5a8a1f]" />
                </div>
                <span><span className="font-semibold text-[#0d1f14]">{t('seasonDetail.details.seasonStart')}</span> <span className="text-zinc-700">{fmtDate(season.seasonStart)}</span></span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                  <CalendarIcon className="h-4 w-4 text-[#5a8a1f]" />
                </div>
                <span><span className="font-semibold text-[#0d1f14]">{t('seasonDetail.details.seasonEnd')}</span> <span className="text-zinc-700">{fmtDate(season.seasonEnd)}</span></span>
              </div>
              {season.waiverText && (
                <div className="flex items-start gap-3 text-sm">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                    <DocumentTextIcon className="h-4 w-4 text-[#5a8a1f]" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-[#0d1f14]">{t('seasonDetail.details.waiver')}</p>
                    <p className="text-xs text-zinc-700 mt-0.5 leading-relaxed line-clamp-3 whitespace-pre-wrap">{season.waiverText}</p>
                    <p className="mt-1 text-[11px] leading-snug text-amber-700">
                      {t('seasonDetail.details.waiverSample')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Pricing Tiers — read-only summary, only for tiered seasons. */}
        <PricingTiersPanel season={season} />

        {/* Danger Zone */}
        <div className="rounded-2xl border border-red-200 bg-red-50 p-5">
          <h2
            className="text-[11px] font-bold uppercase tracking-[1.4px] text-red-700 flex items-center gap-1.5 mb-3"
            style={{ fontFamily: HEAD_FONT }}
          >
            <ExclamationTriangleIcon className="h-4 w-4" />
            {t('seasonDetail.danger.title')}
          </h2>

          {confirmArchive ? (
            <div className="space-y-3">
              <p className="text-sm text-red-700">
                {isArchived
                  ? t('seasonDetail.danger.confirmRestore', { name: season.name })
                  : t('seasonDetail.danger.confirmArchive', { name: season.name })}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleArchive}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-bold uppercase transition-colors"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  {isArchived ? t('seasonDetail.danger.yesRestore') : t('seasonDetail.danger.yesArchive')}
                </button>
                <button
                  onClick={() => setConfirmArchive(false)}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-red-200 bg-white px-4 py-2 text-sm font-bold uppercase text-red-600 hover:bg-red-100 transition-colors"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  {t('seasonDetail.danger.cancel')}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-red-800">
                  {isArchived ? t('seasonDetail.danger.restoreTitle') : t('seasonDetail.danger.archiveTitle')}
                </p>
                <p className="text-xs text-red-600 mt-0.5">
                  {isArchived
                    ? t('seasonDetail.danger.restoreDesc')
                    : t('seasonDetail.danger.archiveDesc')}
                </p>
              </div>
              <button
                onClick={() => setConfirmArchive(true)}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-xl border border-red-300 bg-white px-4 py-2 text-sm font-bold uppercase text-red-700 hover:bg-red-100 transition-colors"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                {isArchived
                  ? <><ArrowUturnLeftIcon className="h-4 w-4" />{t('seasonDetail.danger.restoreBtn')}</>
                  : <><ArchiveBoxIcon className="h-4 w-4" />{t('seasonDetail.danger.archiveBtn')}</>}
              </button>
            </div>
          )}
        </div>

        {/* Invite Modal */}
        {showInviteModal && (
          <InviteModal
            season={season}
            teamId={season.teamId}
            seasonId={seasonId}
            onClose={() => setShowInviteModal(false)}
          />
        )}
      </div>
    </div>
  );
}

/* ── Pricing Tiers panel ────────────────────────────────────
 * Read-only summary of the season's time-based pricing tiers.
 * Renders only when season.pricingMode === 'tiered'. The currently-
 * active tier (resolved by the same algorithm the parent flow uses)
 * gets a lime border + "Active now" badge so coaches can verify what
 * parents are seeing without leaving this page. To edit, use the
 * coach editor (CreateSeason → tier editor).
 */
function PricingTiersPanel({ season }) {
  const { t } = useTranslation();
  if (!season || season.pricingMode !== 'tiered' || !Array.isArray(season.pricingTiers) || season.pricingTiers.length === 0) {
    return null;
  }

  const tiers = [...season.pricingTiers].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
  );

  const active = getActivePricingTier(season.pricingTiers, { now: new Date() });
  const activeId = active?.id || null;

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h2
          className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
          style={{ fontFamily: HEAD_FONT }}
        >
          {t('seasonDetail.tiers.title')}
        </h2>
        <span className="text-[11px] text-zinc-400">{t('seasonDetail.tiers.count', { count: tiers.length })}</span>
      </div>
      <div className="space-y-2">
        {tiers.map((tier) => {
          const isActive = tier.id === activeId;
          const startsLabel = fmtTierDate(tier.startsAt);
          const endsLabel   = fmtTierDate(tier.endsAt);
          return (
            <div
              key={tier.id}
              className={clsx(
                'rounded-xl border p-3 transition-colors',
                isActive
                  ? 'border-2 border-[#b5f03d] bg-[#b5f03d]/10'
                  : 'border-zinc-200 bg-white'
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p
                      className="text-sm font-bold uppercase text-[#0d1f14] truncate"
                      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.4px' }}
                    >
                      {tier.name}
                    </p>
                    {isActive && (
                      <span
                        className="inline-flex items-center gap-1 rounded-full bg-[#b5f03d] px-2 py-0.5 text-[10px] font-bold uppercase text-[#0d1f14]"
                        style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                      >
                        <SparklesIcon className="h-3 w-3" />
                        {t('seasonDetail.tiers.activeNow')}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">
                    {startsLabel || '—'} → {endsLabel || '—'}
                    {tier.maxRegistrations != null
                      ? ` · ${t('seasonDetail.tiers.cap', { count: tier.maxRegistrations })}`
                      : ` · ${t('seasonDetail.tiers.unlimited')}`}
                    {typeof tier.sortOrder === 'number' ? ` · ${t('seasonDetail.tiers.order', { count: tier.sortOrder })}` : ''}
                  </p>
                </div>
                <p
                  className="text-lg font-bold text-[#0d1f14] shrink-0"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  {formatTierPrice(tier.priceInCents)}
                </p>
              </div>
            </div>
          );
        })}
      </div>
      {!active && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          {t('seasonDetail.tiers.noActive')}
        </p>
      )}
    </div>
  );
}

/* ── Invite Modal ────────────────────────────────────────── */
function InviteModal({ season, teamId, seasonId, onClose }) {
  const { t, i18n } = useTranslation();
  const [copied,    setCopied]    = useState(false);
  const [copiedMsg, setCopiedMsg] = useState(false);
  const [tab,       setTab]       = useState('share'); // 'share' | 'email'
  // Language for the registration-invite EMAIL (independent of the coach's
  // UI language). Defaults to the coach's current app language, but they
  // can flip it to match the family they're emailing.
  const [emailLang, setEmailLang] = useState(
    String(i18n.language || '').toLowerCase().startsWith('es') ? 'es' : 'en'
  );

  // Email sender state
  const [emailInput,  setEmailInput]  = useState('');
  const [sending,     setSending]     = useState(false);
  const [sentResults, setSentResults] = useState(null); // [{ email, sent, error }]

  const registerUrl  = `${window.location.origin}/register/${teamId}/${seasonId}`;
  const fee          = season.registrationFee > 0 ? `$${(season.registrationFee / 100).toFixed(0)}` : t('seasonDetail.free');
  const closeDate    = toSeasonDate(season.registrationClose)
    ?.toLocaleDateString(activeLocale(), { month: 'long', day: 'numeric', year: 'numeric' }) || null;
  const whatsIncluded = season.whatsIncluded || [];

  // Payment options text
  const payOpts = [];
  if (season.registrationFee > 0) payOpts.push(`Pay in full: ${fee}`);
  if (season.paymentPlanEnabled) {
    // New format: single fixed installment count
    const n = Number(season.paymentPlanInstallments);
    if (Number.isFinite(n) && n >= 2) {
      const freqLabel = season.paymentPlanFrequency === 'weekly'
        ? 'weekly'
        : season.paymentPlanFrequency === 'biweekly'
          ? 'every 2 weeks'
          : 'monthly';
      payOpts.push(`${n} ${freqLabel} installments available`);
    } else if (Array.isArray(season.paymentPlanOptions) && season.paymentPlanOptions.length) {
      // Legacy format
      season.paymentPlanOptions.forEach(x => payOpts.push(`${x} installments available`));
    }
  }

  // WhatsApp message
  const waText = [
    t('seasonDetail.invite.waTitle', { name: season.name }),
    ``,
    t('seasonDetail.invite.waTeam', { team: season.teamId }),
    season.registrationFee > 0 ? t('seasonDetail.invite.waFee', { fee }) : t('seasonDetail.invite.waFree'),
    closeDate ? t('seasonDetail.invite.waCloses', { date: closeDate }) : null,
    ``,
    whatsIncluded.length ? `${t('seasonDetail.invite.waIncluded')}\n${whatsIncluded.map(i => `• ${i}`).join('\n')}` : null,
    ``,
    t('seasonDetail.invite.waRegisterHere'),
    registerUrl,
  ].filter(Boolean).join('\n');

  const waUrl  = `https://wa.me/?text=${encodeURIComponent(waText)}`;
  const smsUrl = `sms:?body=${encodeURIComponent(waText)}`;

  const copyLink = () => {
    navigator.clipboard.writeText(registerUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const copyMessage = () => {
    navigator.clipboard.writeText(waText).then(() => {
      setCopiedMsg(true);
      setTimeout(() => setCopiedMsg(false), 2000);
    });
  };

  const sendEmails = async () => {
    const raw = emailInput.split(/[\n,;]+/).map(e => e.trim()).filter(Boolean);
    const valid = raw.filter(e => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
    if (valid.length === 0) { toast.error(t('seasonDetail.toast.noValidEmail')); return; }
    setSending(true);
    setSentResults(null);
    try {
      const fn = httpsCallable(getFunctions(), 'sendRegistrationInvites');
      const result = await fn({ seasonId, emails: valid, language: emailLang });
      setSentResults(result.data.results ?? []);
      const sentCount = (result.data.results ?? []).filter(r => r.sent).length;
      toast.success(t('seasonDetail.toast.invitesSent', { count: valid.length, sent: sentCount, total: valid.length }));
    } catch (err) {
      toast.error(err.message || t('seasonDetail.toast.sendFail'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto border border-zinc-200">

        {/* Header — pitch-dark with lime medallion */}
        <div className="px-5 pt-5 pb-4 bg-[#0d1f14] relative overflow-hidden">
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage:
                'linear-gradient(to right, #b5f03d 1px, transparent 1px), linear-gradient(to bottom, #b5f03d 1px, transparent 1px)',
              backgroundSize: '32px 32px',
            }}
          />
          <div className="relative flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                <EnvelopeIcon className="h-5 w-5 text-[#b5f03d]" />
              </div>
              <div className="min-w-0">
                <p
                  className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#b5f03d] mb-0.5"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  {t('seasonDetail.invite.eyebrow')}
                </p>
                <p className="text-sm text-white/80 truncate">{season.name}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="shrink-0 p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-zinc-200 px-5">
          {[
            { key: 'share', label: t('seasonDetail.invite.tabShare') },
            { key: 'email', label: t('seasonDetail.invite.tabEmail') },
          ].map(tabItem => (
            <button
              key={tabItem.key}
              onClick={() => setTab(tabItem.key)}
              className={clsx(
                'px-4 py-3 text-xs font-bold uppercase border-b-2 transition-colors -mb-px',
                tab === tabItem.key
                  ? 'border-[#b5f03d] text-[#3d6010]'
                  : 'border-transparent text-zinc-400 hover:text-[#5a8a1f]'
              )}
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
            >
              {tabItem.label}
            </button>
          ))}
        </div>

        {/* Email Preview — shown on Share tab */}
        {tab === 'share' && <div className="p-5 space-y-4">
          <p
            className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
            style={{ fontFamily: HEAD_FONT }}
          >
            {t('seasonDetail.invite.emailPreview')}
          </p>

          {/* Mini email preview card */}
          <div className="rounded-xl border border-zinc-200 overflow-hidden text-sm">
            {/* Mini header — pitch-dark */}
            <div className="bg-[#0d1f14] px-4 py-3 text-center relative overflow-hidden">
              <div
                className="pointer-events-none absolute inset-0 opacity-[0.05]"
                style={{
                  backgroundImage:
                    'linear-gradient(to right, #b5f03d 1px, transparent 1px), linear-gradient(to bottom, #b5f03d 1px, transparent 1px)',
                  backgroundSize: '32px 32px',
                }}
              />
              <p className="relative text-white font-black text-base" style={{ fontFamily: HEAD_FONT }}>{season.name}</p>
              <p className="relative text-[#b5f03d] text-[11px] font-bold uppercase tracking-[1.2px] mt-0.5" style={{ fontFamily: HEAD_FONT }}>{t('seasonDetail.invite.regNowOpen')}</p>
            </div>
            <div className="px-4 py-4 bg-white space-y-3">
              <p className="text-zinc-600 text-xs leading-relaxed">
                {t('seasonDetail.invite.previewIntro')}
              </p>

              {whatsIncluded.length > 0 && (
                <div className="bg-[#f7faf5] rounded-lg p-3">
                  <p className="text-[10px] font-bold text-[#5a8a1f] uppercase tracking-wide mb-2" style={{ fontFamily: HEAD_FONT }}>{t('seasonDetail.invite.whatsIncluded')}</p>
                  {whatsIncluded.map((item, i) => (
                    <div key={i} className="flex items-start gap-1.5 mb-1">
                      <span className="text-green-600 text-[10px] font-bold flex-shrink-0 mt-0.5">✓</span>
                      <span className="text-xs text-zinc-700">{item}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="bg-[#b5f03d]/10 ring-1 ring-[#b5f03d]/40 rounded-lg p-3 flex justify-between items-center">
                <div>
                  <p className="text-[10px] font-bold text-[#5a8a1f] uppercase tracking-wide" style={{ fontFamily: HEAD_FONT }}>{t('seasonDetail.invite.fee')}</p>
                  <p className="text-sm font-black text-[#3d6010]">{fee}</p>
                </div>
                {payOpts.length > 1 && (
                  <div className="text-right">
                    <p className="text-[10px] font-bold text-[#5a8a1f] uppercase tracking-wide" style={{ fontFamily: HEAD_FONT }}>{t('seasonDetail.invite.options')}</p>
                    <p className="text-xs text-zinc-700">{t('seasonDetail.invite.plansAvailable')}</p>
                  </div>
                )}
              </div>

              {closeDate && (
                <p className="text-xs text-red-600 font-semibold">{t('seasonDetail.invite.closesAt', { date: closeDate })}</p>
              )}

              <div className="text-center pt-1">
                <span
                  className="inline-block bg-[#b5f03d] text-[#0d1f14] text-xs font-bold uppercase px-6 py-2 rounded-full"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                >
                  {t('seasonDetail.invite.registerNow')}
                </span>
              </div>
            </div>
          </div>

          {/* Share buttons */}
          <p
            className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] pt-1"
            style={{ fontFamily: HEAD_FONT }}
          >
            {t('seasonDetail.invite.shareVia')}
          </p>
          <div className="grid grid-cols-2 gap-2">
            <a href={waUrl} target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#25D366] text-white text-sm font-bold uppercase hover:bg-[#1ebe57] transition-colors"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              <svg className="h-4 w-4 fill-white flex-shrink-0" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                <path d="M12 0C5.373 0 0 5.373 0 12c0 2.125.555 4.122 1.524 5.855L0 24l6.29-1.49A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.9a9.9 9.9 0 01-5.031-1.374l-.361-.214-3.735.885.897-3.64-.235-.374A9.861 9.861 0 012.1 12C2.1 6.526 6.526 2.1 12 2.1S21.9 6.526 21.9 12 17.474 21.9 12 21.9z"/>
              </svg>
              WhatsApp
            </a>
            <a href={smsUrl}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#0d1f14] text-white text-sm font-bold uppercase hover:bg-[#1a3325] transition-colors"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              <svg className="h-4 w-4 fill-white flex-shrink-0" viewBox="0 0 24 24">
                <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
              </svg>
              SMS
            </a>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={copyLink}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-zinc-200 bg-white text-[#0d1f14] text-sm font-bold uppercase hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              <LinkIcon className="h-4 w-4" />
              {copied ? t('seasonDetail.invite.copied') : t('seasonDetail.invite.copyLink')}
            </button>
            <button onClick={copyMessage}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-zinc-200 bg-white text-[#0d1f14] text-sm font-bold uppercase hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              <EnvelopeIcon className="h-4 w-4" />
              {copiedMsg ? t('seasonDetail.invite.copied') : t('seasonDetail.invite.copyMessage')}
            </button>
          </div>

          <p className="text-[11px] text-zinc-400 text-center">
            {t('seasonDetail.invite.regLink')} <span className="font-mono text-zinc-500 text-[10px] break-all">{registerUrl}</span>
          </p>
        </div>}

        {/* Email Sender Tab */}
        {tab === 'email' && (
          <div className="p-5 space-y-4">
            {/* Email-language toggle — sends the invite in the family's
                language regardless of the coach's own UI language. */}
            <div className="flex items-center justify-between gap-3">
              <span className="text-[11px] font-bold uppercase tracking-[1.2px] text-[#5a8a1f]" style={{ fontFamily: HEAD_FONT }}>
                {t('seasonDetail.invite.emailLanguage')}
              </span>
              <div className="inline-flex rounded-lg border border-zinc-200 bg-zinc-50 p-0.5">
                {[['en', 'English'], ['es', 'Español']].map(([code, label]) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setEmailLang(code)}
                    disabled={sending}
                    className={clsx(
                      'px-3 py-1.5 rounded-md text-xs font-bold transition-colors',
                      emailLang === code ? 'bg-white text-[#0d1f14] shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#0d1f14] mb-1">{t('seasonDetail.invite.parentEmails')}</p>
              <p className="text-xs text-zinc-500 mb-3">
                {t('seasonDetail.invite.emailsHelp')}
              </p>
              <textarea
                value={emailInput}
                onChange={e => setEmailInput(e.target.value)}
                rows={6}
                placeholder={"parent1@example.com\nparent2@example.com, parent3@example.com"}
                disabled={sending}
                className="w-full rounded-xl border border-zinc-200 px-4 py-3 text-sm focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30 resize-none font-mono placeholder:font-sans placeholder:text-zinc-300"
              />
            </div>

            {/* Preview of what they'll receive */}
            <div className="rounded-xl bg-[#b5f03d]/10 border border-[#b5f03d]/40 p-4 space-y-2">
              <p
                className="text-[11px] font-bold text-[#5a8a1f] uppercase tracking-[1.4px]"
                style={{ fontFamily: HEAD_FONT }}
              >
                {t('seasonDetail.invite.theyllReceive')}
              </p>
              <div className="space-y-1 text-xs text-zinc-700">
                <div className="flex items-start gap-2">
                  <span className="text-[#5a8a1f] font-bold">→</span>
                  <span>{t('seasonDetail.invite.receiveBranded')}</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-[#5a8a1f] font-bold">→</span>
                  <span>{t('seasonDetail.invite.receiveDetails')}</span>
                </div>
                {toSeasonDate(season.registrationClose) && (
                  <div className="flex items-start gap-2">
                    <span className="text-red-600 font-bold">→</span>
                    <span>{t('seasonDetail.invite.receiveDeadline', { date: toSeasonDate(season.registrationClose)?.toLocaleDateString(activeLocale(), { month: 'long', day: 'numeric' }) })}</span>
                  </div>
                )}
                <div className="flex items-start gap-2">
                  <span className="text-[#5a8a1f] font-bold">→</span>
                  <span>{t('seasonDetail.invite.receiveButton')}</span>
                </div>
              </div>
            </div>

            {/* Send results */}
            {sentResults && (
              <div className="rounded-xl border border-zinc-200 divide-y divide-zinc-100 overflow-hidden">
                {sentResults.map((r, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm">
                    <span className="text-zinc-700 font-mono text-xs truncate">{r.email}</span>
                    {r.sent
                      ? <span className="text-green-600 font-semibold text-xs flex items-center gap-1">{t('seasonDetail.invite.sent')}</span>
                      : <span className="text-red-600 text-xs">{t('seasonDetail.invite.failed')}</span>
                    }
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={sendEmails}
              disabled={sending || !emailInput.trim()}
              className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-50"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
            >
              <EnvelopeIcon className="h-4 w-4" />
              {sending ? t('seasonDetail.invite.sending') : t('seasonDetail.invite.sendInvites')}
            </button>

            <p className="text-[11px] text-zinc-400 text-center">
              {t('seasonDetail.invite.sentVia')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
